import isIBAN from "validator/es/lib/isIBAN";
export default function () {
  const { t } = useI18n();
  const runtimeConfig = useRuntimeConfig();
  function convertValue (v: any) : any {
    if (typeof v === "string") {
      return parseFloat(v.replace(/,/g, ".").replace(/\s+/g, "")); // convert input with coma to dot e.g. 5,5 => 5.5
    }
    return v;
  }

  const postCodeRegex = /^\d{5}$|^\d{3} \d{2}$/;

  function testImei (value: any) {
    const len = value.length;
    const parity = len % 2;
    let sum = 0;
    for (let i = len - 1; i >= 0; i--) {
      let d = parseInt(value.charAt(i), 10);
      if (i % 2 === parity) {
        d *= 2;
      }
      if (d > 9) {
        d -= 9;
      }
      sum += d;
    }
    return sum % 10 === 0;
  }

  function testBankAccountNumber (value : any) {
    const match = value.match(/^((\d{0,6})-)?(\d{1,10})$/);
    if (!match) { return false; }

    const rawPrefix = match[2] || "";
    const rawBankNumber = match[3] || "";
    const prefix = rawPrefix.padStart(6, "0");
    const bankNumber = rawBankNumber.padStart(10, "0");

    const isOkFirst = (10 * prefix[0] + 5 * prefix[1] + 8 * prefix[2] + 4 * prefix[3] + 2 * prefix[4] + 1 * prefix[5]) % 11 === 0;
    if (!isOkFirst) {
      return false;
    }
    return ((6 * bankNumber[0] + 3 * bankNumber[1] + 7 * bankNumber[2] + 9 * bankNumber[3] + 10 * bankNumber[4] + 5 * bankNumber[5] + 8 * bankNumber[6] + 4 * bankNumber[7] + 2 * bankNumber[8] + 1 * bankNumber[9]) % 11 === 0
    );
  }

  const BANK_CODES_BY_REGION: { [key: string]: string[] } = {
    cz: ["0100", "0300", "0600", "0710", "0800", "2010", "2020", "2030", "2060", "2070", "2100", "2200", "2220", "2240", "2250", "2260", "2310", "2600", "2700", "3030", "3050", "3060", "3500", "4000", "4300", "5500", "5800", "6000", "6100", "6200", "6210", "6300", "6363", "6700", "6800", "7910", "7940", "7950", "7960", "7970", "7980", "7990", "8030", "8040", "8060", "8090", "8150", "8200", "8220", "8230", "8240", "8250"],
    sk: ["0200", "0900", "0720", "1100", "1111", "3000", "3100", "5200", "5600", "5900", "6500", "7300", "7500", "7930", "8050", "8100", "8120", "8130", "8170", "8160", "8180", "8191", "8400", "8320", "8330", "8360", "8370", "8390", "8420", "8430", "9950", "9952", "2010", "9953", "8440", "5800", "4000", "8450"]
  };

  function getBankCodes (region: string): string[] {
    if (region in BANK_CODES_BY_REGION) {
      return BANK_CODES_BY_REGION[region];
    }
    return Object.values(BANK_CODES_BY_REGION).flat();
  }

  const BANK_CODES = getBankCodes(runtimeConfig.public.region);

  function testBankCode (value: any) {
    return BANK_CODES.includes(value);
  }

  function testCsv (value: any) {
    return (value && value?.type && value?.type === "text/csv");
  }

  function ensureArray<T>(value: T[]|T): T[] {
    if (!Array.isArray(value)) {
      return [value];
    }
    return value;
  }

  const { humanFileSize } = useFile();

  const rules = {
    required: (value: any): boolean | string => (value !== null && value !== undefined && value !== "") || t("form.validation.required"),
    requiredArray: (value: any): boolean | string => (Array.isArray(value) && value?.length > 0) || t("form.validation.required"),
    requiredTrue: (value: any): boolean | string => (value === true) || t("form.validation.required"),
    max: (maxLength: number) => (value: any): boolean | string => !value || (value && value.length <= maxLength) || t("form.validation.max", { maxLength }),
    min: (minLength: number) => (value: any): boolean | string => !value || (value && value.length >= minLength) || t("form.validation.min", { minLength }),
    maxValue: (maxValue: number) => (value: any): boolean | string => (value !== undefined && value !== null && (convertValue(value) <= maxValue)) || t("form.validation.maxValue", { maxValue }),
    minValue: (minValue: number) => (value: any): boolean | string => (value !== undefined && value !== null && (convertValue(value) >= minValue)) || t("form.validation.minValue", { minValue }),
    maxValueFloat: (maxValue: number) => (value: any): boolean | string => (value !== undefined && value !== null && (convertValue(value) <= maxValue)) || t("form.validation.maxValue", { maxValue }),
    minValueFloat: (minValue: number) => (value: any): boolean | string => (value !== undefined && value !== null && (convertValue(value) >= minValue)) || t("form.validation.minValue", { minValue }),
    isDecimal: (value: any): boolean | string => !value || (value && /^\d*([,.]\d+)?$/.test(value)) || t("form.validation.isNumber"),
    isNumber: (value: any): boolean | string => !value || (value && /^\d*$/.test(value)) || t("form.validation.isNumber"),
    isPositiveNumber: (value: any): boolean | string => !value || (value && /^\d+$/.test(value)) || t("form.validation.isPositiveNumber"),
    isPositiveFloatOrDecimalNumber: (value: any): boolean | string => !value || (value && /^\d+(\.\d+)?$/.test(value)) || t("form.validation.isPositiveFloatOrDecimalNumber"),
    valueIsNotOnlyNumbers: (value: any): boolean | string => !value || (value && !/^\d*$/.test(value)) || t("form.validation.valueIsNotOnlyNumbers"),
    postCode: (value: any): boolean | string => !value || (value && postCodeRegex.test(value)) || t("form.validation.postCode"),
    isEmail: (value: any): boolean | string => {
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!value) { return true; }
      return pattern.test(value as string) || t("form.validation.email");
    },
    isPhoneNumber: (value: any): boolean | string => !value || (!!value && /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/.test(value as string)) || t("form.validation.isPhoneNumber"),
    isLink: (value: any): boolean | string => !value || (!!value && /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)?/gi.test(value as string)) || t("form.validation.isLink"),
    isIMEIValid: (value: any): true | string => !value || (value && testImei(value)) || t("form.validation.isIMEIValid"),
    bankAccountNumber: (value: any): boolean | string => !value || (value && testBankAccountNumber(value)) || t("form.validation.bankAccountNumber"),
    bankCode: (value: any): boolean | string => !value || (value && testBankCode(value)) || t("form.validation.bankCode"),
    iban: (value: any): boolean | string => !value || (value && isIBAN(value)) || t("form.validation.iban"),
    nameAndSurname: (value: any): boolean | string => !value || (!!value && /^\S+\s+\S+[a-zA-Z., ]*$/.test(value as string)) || t("form.validation.nameAndSurname"),
    maxLength: (maxLength: number) => (value:any): boolean | string => !value || (!!value && value.length < maxLength) || t("form.validation.maxLength", { maxLength }),
    isCsv: (value: any): boolean | string => !value || (value && testCsv(value)) || t("form.validation.hasToBeCsv"),

    hasAllowedExtensions: (allowedExtensions: string[]) => (files: File[]|File): boolean | string => {
      files = ensureArray(files);
      const regex = new RegExp("(" + allowedExtensions.join("|").replace(/\./g, "\\.") + ")$");
      return (files.length && files.every(f => regex.test(f.type))) || t("form.validation.invalid_file_type_extension", { allowedExtensions: allowedExtensions.join(", ") });
    },
    maxFileSize: (maxFileSize: number) => (files: any): boolean | string => {
      files = ensureArray(files);
      return (files.length && files.every((f: any) => f.size < maxFileSize)) || t("form.validation.invalid_file_size", {maxFileSize: humanFileSize(maxFileSize, false, 0)});
    },
    isHHmm: (value: any): boolean | string => {
      return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value) || t("dialogs.bonus_detail.invalid_time_format");
    }
  };

  return {
    rules,
    testImei
  };
}
